import { combineEpics } from 'redux-observable';
import { filter, map, mapTo, switchMap } from 'rxjs';
import { isOfType } from 'typesafe-actions';
import { catchAxiosError, catchUnauthorizedError, createEpic$ } from 'lib/store';
import { notificationError, notificationSuccess } from 'store/notifications';
import { systemUnauthorized } from 'store/system';
import {
  AccountAction,
  accountCreated,
  accountError,
  accountListResponse,
  accountResponse,
  accountSummaryResponse,
  accountUpdated,
} from './actions';

const accountCreateEpic$ = createEpic$(({ action$, createAccount$ }) =>
  action$.pipe(
    filter(isOfType(AccountAction.Create)),
    switchMap(action =>
      createAccount$(action.payload).pipe(
        map(accountCreated),
        catchAxiosError(accountError),
        catchUnauthorizedError(systemUnauthorized),
      ),
    ),
  ),
);

const accountErrorEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(AccountAction.Error)),
    mapTo(
      notificationError({
        caption: 'Error',
        duration: 10000,
        message: 'There was a problem loading account details',
      }),
    ),
  ),
);

const accountListRequestEpic$ = createEpic$(({ action$, getAccountList$ }) =>
  action$.pipe(
    filter(isOfType(AccountAction.ListRequest)),
    switchMap(() =>
      getAccountList$().pipe(
        map(accountListResponse),
        catchAxiosError(accountError),
        catchUnauthorizedError(systemUnauthorized),
      ),
    ),
  ),
);

const accountRequestEpic$ = createEpic$(({ action$, getAccount$ }) =>
  action$.pipe(
    filter(isOfType(AccountAction.Request)),
    switchMap(action =>
      getAccount$(action.payload).pipe(
        map(accountResponse),
        catchAxiosError(accountError),
        catchUnauthorizedError(systemUnauthorized),
      ),
    ),
  ),
);

const accountSummaryRequestEpic$ = createEpic$(({ action$, getAccountSummary$ }) =>
  action$.pipe(
    filter(isOfType(AccountAction.SummaryRequest)),
    switchMap(() =>
      getAccountSummary$().pipe(
        map(accountSummaryResponse),
        catchAxiosError(accountError),
        catchUnauthorizedError(systemUnauthorized),
      ),
    ),
  ),
);

const accountUpdateEpic$ = createEpic$(({ action$, updateAccount$ }) =>
  action$.pipe(
    filter(isOfType(AccountAction.Update)),
    switchMap(action =>
      updateAccount$(action.payload.id, action.payload.data).pipe(
        map(accountUpdated),
        catchAxiosError(accountError),
        catchUnauthorizedError(systemUnauthorized),
      ),
    ),
  ),
);

const accountUpdatedEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(AccountAction.Updated)),
    mapTo(
      notificationSuccess({
        caption: 'Success',
        message: 'Changes successfully saved',
      }),
    ),
  ),
);

export default combineEpics(
  accountCreateEpic$,
  accountErrorEpic$,
  accountListRequestEpic$,
  accountRequestEpic$,
  accountSummaryRequestEpic$,
  accountUpdateEpic$,
  accountUpdatedEpic$,
);
